import React, { useEffect, useState, useCallback } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import successToast from "../../components/toasts/successToast";
import { useDispatch } from "react-redux";
import {
    addUserAction,
    userEdit,
    userView,
} from "../../redux/actions/userAction";
import errorToast from "../../components/toasts/errorToast";
import { Spinner } from "react-bootstrap";
import { salutation } from "../../components/data/salutation";
import { IoArrowBack } from "react-icons/io5";
import { useAuth } from '../../context/AuthContext';
import { adminGetUserSessionsApi, adminGetUserSessionHistoryApi, adminTerminateSessionApi, adminTerminateAllSessionsApi } from "../../redux/apis/sessionApi";
import moment from "moment";

const AddUser = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [userData, setUserData] = useState({});
    const [disable, setDisable] = useState(false);
    const [signIn, setSignIn] = useState('');
    const [sessions, setSessions] = useState([]);
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [terminatingId, setTerminatingId] = useState(null);
    const [terminatingAll, setTerminatingAll] = useState(false);
    const [activeTab, setActiveTab] = useState('active');
    const [historyPage, setHistoryPage] = useState(1);
    const [historyData, setHistoryData] = useState({ sessions: [], total: 0, page: 1, totalPages: 0 });
    const [loadingHistory, setLoadingHistory] = useState(false);
    const currentPath = window.location.pathname;
    const segments = currentPath.split("/");
    const usersSegment = segments[1];
    const { isAdmin, isTeamLead } = useAuth();

    const onViewSuccess = (data) => {
        setUserData(data.data.data);
        setLoading(false);
    };

    const onViewError = (data) => {
        errorToast(data.data.message);
        setLoading(false);
    };

    useEffect(() => {
        if (id) {
            setLoading(true);
            dispatch(userView({ id }, onViewSuccess, onViewError));
        }
        if (id && usersSegment === "view-user") {
            setDisable(true);
        }
    }, [id, dispatch]);

    const initialValues = {
        id: userData?.id || "",
        salutation: userData?.salutation || "",
        first_name: userData?.first_name || "",
        middle_name: userData?.middle_name || "",
        last_name: userData?.last_name || "",
        email: userData?.email || "",
        sign_in: userData?.sign_in || "",
        user_type: userData?.user_type || "",
        password: userData?.password || "",
        confirm_password: "",
    };

    const passwordRegex = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const validationSchema = Yup.object().shape({
        salutation: Yup.string().required("Required"),
        first_name: Yup.string().required("Required"),
        last_name: Yup.string().required("Required"),
        email: Yup.string().matches(emailRegex, "Invalid Email Format").required("Required"),
        sign_in: Yup.string().required("Required"),
        user_type: Yup.string().required("Required"),
        password: signIn === "email" ? Yup.string()
            .required("Required")
            .matches(passwordRegex, "Password criteria not matching") : "",
        confirm_password: signIn === "email" ? Yup.string()
            .required("Required")
            .oneOf([Yup.ref("password"), null], "Passwords must match") : "",
    });

    const onSuccess = (data) => {
        successToast(data.data.message);
        setLoading(false);
        navigate("/users");
    };

    const onError = (data) => {
        setLoading(false);
        errorToast(data.data.message);
    };

    const fetchSessions = useCallback(async () => {
        if (!id || usersSegment !== 'view-user') return;
        setLoadingSessions(true);
        try {
            const res = await adminGetUserSessionsApi(id);
            if (res?.data?.data?.sessions) setSessions(res.data.data.sessions);
        } catch (_) {} finally {
            setLoadingSessions(false);
        }
    }, [id, usersSegment]);

    const fetchHistory = useCallback(async (page) => {
        if (!id || usersSegment !== 'view-user') return;
        setLoadingHistory(true);
        try {
            const res = await adminGetUserSessionHistoryApi(id, page);
            if (res?.data?.data) setHistoryData(res.data.data);
        } catch (_) {} finally {
            setLoadingHistory(false);
        }
    }, [id, usersSegment]);

    useEffect(() => { fetchSessions(); }, [fetchSessions]);
    useEffect(() => { if (activeTab === 'history') fetchHistory(historyPage); }, [activeTab, historyPage, fetchHistory]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === 'history') setHistoryPage(1);
    };

    const handleTerminateSession = async (sessionId) => {
        setTerminatingId(sessionId);
        try {
            const res = await adminTerminateSessionApi(id, sessionId);
            if (res?.data?.statusCode === 200) {
                successToast(res.data.message);
                fetchSessions();
            } else {
                errorToast(res?.data?.message || 'Failed');
            }
        } catch (_) {
            errorToast('Failed to terminate session');
        } finally {
            setTerminatingId(null);
        }
    };

    const handleTerminateAll = async () => {
        setTerminatingAll(true);
        try {
            const res = await adminTerminateAllSessionsApi(id);
            if (res?.data?.statusCode === 200) {
                successToast(res.data.message);
                fetchSessions();
            } else {
                errorToast(res?.data?.message || 'Failed');
            }
        } catch (_) {
            errorToast('Failed to terminate sessions');
        } finally {
            setTerminatingAll(false);
        }
    };

    const terminatedByLabel = (raw) => {
        if (!raw) return 'Inactive';
        if (raw === 'logout') return 'Logged Out';
        if (raw === 'policy') return 'Policy Enforced';
        if (raw === 'user_remote') return 'Signed Out Remotely';
        if (raw === 'expired') return 'Token Expired';
        if (raw === 'admin') return 'Terminated by Admin';
        return raw;
    };

    const onSubmit = (values) => {
        console.log("form values", values);
        if (usersSegment === "edit-user") {
            dispatch(userEdit(values, onSuccess, onError));
        } else {
            dispatch(addUserAction(values, onSuccess, onError));
        }
    };

    const pathLabels = {
        "edit-user": "Edit User",
        "view-user": "View User",
        "add-user": "Add User",
    };

    if (!isAdmin || isTeamLead) {
        return (
            <div className="w-100 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
                <i className="bi bi-shield-lock fs_40 text-danger mb-3"></i>
                <h5 className="fw_600">Access Denied</h5>
                <p className="text-muted fs_14">This page is only accessible to company administrators and team leads.</p>
            </div>
        );
    }

    return (
        <div className="d-flex vh-100 overflow-auto">
            <div className="w-100 ">
                <div className="w-100 d-flex align-items-center header border-bottom position-sticky top-0 bg-white py-2 z-1">
                    <span className="px-1 fs_16 cursor_pointer pb-1" onClick={() => navigate(-1)}><IoArrowBack /></span>
                    <h2 className="fs_20 fw_400 fn_Roboto text-start px-2 pt-1">
                        {pathLabels[usersSegment]}
                    </h2>
                </div>
                {loading ?
                    <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: "90%" }}>
                        <Spinner animation="border" variant="primary" /> <span>Loading...</span>
                    </div>
                    :
                    <div className="flex-grow-1 pt-3">
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={onSubmit}
                            enableReinitialize
                        >
                            {({ values, setFieldValue, isValid, dirty }) => (
                                <Form className="d-flex flex-column h-100 justify-content-between">
                                    <div>
                                        <div className="text-start gap-3 mb-2 text-nowrap mx-4">
                                            <label htmlFor="salutation" className="py-1">
                                                Salutation
                                            </label>
                                            <div className="d-flex position-relative">
                                                <Field
                                                    as="select"
                                                    id="salutation"
                                                    disabled={disable}
                                                    name="salutation"
                                                    value={values.salutation}
                                                    className="input-field"
                                                    style={{ width: "20rem" }}
                                                >
                                                    <option value="">Select Salutation</option>
                                                    {salutation.map((sal) => (
                                                        <option key={sal.value} value={sal.value}>
                                                            {sal.label}
                                                        </option>
                                                    ))}
                                                </Field>
                                            </div>
                                            <ErrorMessage
                                                name="salutation"
                                                component="div"
                                                className="text-danger mt-1 fs_12"
                                            />
                                        </div>
                                        <div className="text-start gap-3 mb-2 text-nowrap mx-4">
                                            <label htmlFor="first_name" className="py-1">
                                                First Name
                                            </label>
                                            <div className="d-flex position-relative">
                                                <Field
                                                    type="text"
                                                    disabled={disable}
                                                    placeholder="Enter First Name"
                                                    id="first_name"
                                                    name="first_name"
                                                    value={values.first_name}
                                                    className="input-field"
                                                    style={{ width: "20rem" }}
                                                    onInput={(e) => {
                                                        e.target.value = e.target.value.replace(/[^A-Za-z\s]/g, "");
                                                    }}
                                                />
                                            </div>
                                            <ErrorMessage
                                                name="first_name"
                                                component="div"
                                                className="text-danger mt-1 fs_12"
                                            />
                                        </div>
                                        <div className="text-start gap-3 mb-2 text-nowrap mx-4">
                                            <label htmlFor="middle_name" className="py-1">
                                                Middle Name
                                            </label>
                                            <div className="d-flex position-relative">
                                                <Field
                                                    type="text"
                                                    disabled={disable}
                                                    placeholder="Enter Middle Name"
                                                    id="middle_name"
                                                    name="middle_name"
                                                    value={values.middle_name}
                                                    className="input-field"
                                                    style={{ width: "20rem" }}
                                                    onInput={(e) => {
                                                        e.target.value = e.target.value.replace(/[^A-Za-z\s]/g, "");
                                                    }}
                                                />
                                            </div>
                                            <ErrorMessage
                                                name="middle_name"
                                                component="div"
                                                className="text-danger mt-1 fs_12"
                                            />
                                        </div>
                                        <div className="text-start gap-3 mb-2 text-nowrap mx-4">
                                            <label htmlFor="last_name" className="py-1">
                                                Last Name
                                            </label>
                                            <div className="d-flex position-relative">
                                                <Field
                                                    type="text"
                                                    disabled={disable}
                                                    placeholder="Enter Last Name"
                                                    id="last_name"
                                                    name="last_name"
                                                    value={values.last_name}
                                                    className="input-field"
                                                    style={{ width: "20rem" }}
                                                    onInput={(e) => {
                                                        e.target.value = e.target.value.replace(/[^A-Za-z\s]/g, "");
                                                    }}
                                                />
                                            </div>
                                            <ErrorMessage
                                                name="last_name"
                                                component="div"
                                                className="text-danger mt-1 fs_12"
                                            />
                                        </div>

                                        <div className="text-start gap-3 mb-2 text-nowrap mx-4">
                                            <label htmlFor="email" className="py-1">
                                                Email
                                            </label>
                                            <div className="d-flex position-relative">
                                                <Field
                                                    type="email"
                                                    disabled={disable}
                                                    placeholder="Enter Email"
                                                    id="email"
                                                    name="email"
                                                    value={values.email}
                                                    className="input-field"
                                                    style={{ width: "20rem" }}
                                                />
                                            </div>
                                            <ErrorMessage
                                                name="email"
                                                component="div"
                                                className="text-danger mt-1 fs_12"
                                            />
                                        </div>

                                        <div className="text-start gap-3 mb-2 text-nowrap mx-4">
                                            <label htmlFor="user_type" className="py-1">
                                                User Type
                                            </label>
                                            <div className="d-flex position-relative">
                                                <Field
                                                    as="select"
                                                    id="user_type"
                                                    disabled={disable}
                                                    name="user_type"
                                                    value={values.user_type}
                                                    className="input-field"
                                                    style={{ width: "20rem" }}
                                                >
                                                    <option value="">Select User Type</option>
                                                    <option value="admin">Admin</option>
                                                    <option value="team_lead">Team Lead</option>
                                                    <option value="user">User</option>
                                                </Field>
                                            </div>
                                            <ErrorMessage
                                                name="user_type"
                                                component="div"
                                                className="text-danger mt-1 fs_12"
                                            />
                                        </div>

                                        <div className="text-start gap-3 mb-2 text-nowrap mx-4">
                                            <label htmlFor="sign_in" className="py-1">
                                                Sign-in method
                                            </label>
                                            <div className="d-flex position-relative">
                                                <Field
                                                    as="select"
                                                    id="sign_in"
                                                    name="sign_in"
                                                    disabled={disable}
                                                    value={values.sign_in}
                                                    className="input-field"
                                                    style={{ width: "20rem" }}
                                                    onClick={(e) => setSignIn(e.target.value)}
                                                >
                                                    <option value="">Select Sign-in method</option>
                                                    <option value="google">Google</option>
                                                    <option value="email">Email</option>
                                                </Field>
                                            </div>
                                            <ErrorMessage
                                                name="sign_in"
                                                component="div"
                                                className="text-danger mt-1 fs_12"
                                            />
                                        </div>
                                        {values.sign_in === "email" && !disable && (
                                            <>
                                                <div className="text-start gap-3 mb-2 text-nowrap mx-4">
                                                    <label htmlFor="password" className="py-1">
                                                        Password
                                                    </label>
                                                    <div className="d-flex position-relative">
                                                        <Field
                                                            disabled={disable}
                                                            placeholder="Enter Password"
                                                            id="password"
                                                            name="password"
                                                            type="password"
                                                            value={values.password}
                                                            className="input-field"
                                                            style={{ width: "20rem" }}
                                                        />
                                                    </div>
                                                    <ErrorMessage
                                                        name="password"
                                                        component="div"
                                                        className="text-danger mt-1 fs_12"
                                                    />
                                                </div>

                                                <div className="text-start gap-3 mb-2 text-nowrap mx-4">
                                                    <label htmlFor="confirm_password" className="py-1">
                                                        Confirm Password
                                                    </label>
                                                    <div className="d-flex position-relative">
                                                        <Field
                                                            disabled={disable}
                                                            type="password"
                                                            placeholder="Enter Confirm Password"
                                                            id="confirm_password"
                                                            name="confirm_password"
                                                            value={values.confirm_password}
                                                            className="input-field"
                                                            style={{ width: "20rem" }}
                                                        />
                                                    </div>
                                                    <ErrorMessage
                                                        name="confirm_password"
                                                        component="div"
                                                        className="text-danger mt-1 fs_12"
                                                    />
                                                </div>

                                                <div className='text-start gap-3 mb-2 text-nowrap mx-4' style={{ width: '25rem' }}>
                                                    <p className='text-start text-wrap mb-2 fs_12'>Your password must contain at least one uppercase letter, one lowercase letter, one number, and one special character, with a minimum length of 8 characters (e.g., Pass@123).</p>
                                                </div>
                                            </>
                                        )}


                                    </div>
                                    {loading && !disable && (
                                        <div className="d-flex align-items-center text-center">
                                            <Spinner size="sm" className="text-primary" />{" "}
                                            <span className="ms-2">Updating...</span>
                                        </div>
                                    )}
                                    {!disable && (
                                        <div className="d-flex gap-5 mt-4 mx-4 my-3">
                                            <button
                                                type="submit"
                                                className="btn btn-primary"
                                                style={{ width: "5rem" }}
                                                disabled={!(isValid && dirty)}
                                            >
                                                {usersSegment === "edit-user" ? "Update" : "Create"}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                style={{ width: "5rem" }}
                                                onClick={() => navigate("/users")}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    )}

                                    {disable && isAdmin && (
                                        <div className="mx-4 my-4">
                                            <div className="d-flex align-items-center justify-content-between mb-3">
                                                <div className="d-flex gap-2">
                                                    <button
                                                        type="button"
                                                        className={`btn btn-sm ${activeTab === 'active' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                        onClick={() => handleTabChange('active')}
                                                    >
                                                        Active Sessions{sessions.length > 0 ? ` (${sessions.length})` : ''}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`btn btn-sm ${activeTab === 'history' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                        onClick={() => handleTabChange('history')}
                                                    >
                                                        History{historyData.total > 0 ? ` (${historyData.total})` : ''}
                                                    </button>
                                                </div>
                                                {activeTab === 'active' && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                                        onClick={handleTerminateAll}
                                                        disabled={terminatingAll || sessions.length === 0}
                                                    >
                                                        {terminatingAll ? <Spinner size="sm" /> : <i className="bi bi-shield-x" />}
                                                        {' '}Terminate All
                                                    </button>
                                                )}
                                            </div>

                                            {activeTab === 'active' && (
                                                <div className="card" style={{ overflow: 'hidden' }}>
                                                    {loadingSessions ? (
                                                        <div className="p-3 text-center"><Spinner size="sm" /> Loading sessions...</div>
                                                    ) : (
                                                        <div className="table-responsive">
                                                            <table className="table table-hover mb-0">
                                                                <thead className="table-light">
                                                                    <tr>
                                                                        <th>Device</th>
                                                                        <th>IP Address</th>
                                                                        <th className="text-nowrap">Last Active</th>
                                                                        <th></th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {sessions.length > 0 ? sessions.map((s) => (
                                                                        <tr key={s.id}>
                                                                            <td>{s.device_summary || 'Unknown Device'}</td>
                                                                            <td className="text-muted">{s.ip_address || '—'}</td>
                                                                            <td className="text-nowrap text-muted">{moment(s.last_active_at).fromNow()}</td>
                                                                            <td>
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-sm btn-outline-danger"
                                                                                    onClick={() => handleTerminateSession(s.id)}
                                                                                    disabled={terminatingId === s.id}
                                                                                >
                                                                                    {terminatingId === s.id ? <Spinner size="sm" /> : 'Terminate'}
                                                                                </button>
                                                                            </td>
                                                                        </tr>
                                                                    )) : (
                                                                        <tr>
                                                                            <td colSpan="4" className="text-center text-muted py-4">No active sessions</td>
                                                                        </tr>
                                                                    )}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {activeTab === 'history' && (
                                                <div className="card" style={{ overflow: 'hidden' }}>
                                                    {loadingHistory ? (
                                                        <div className="p-3 text-center"><Spinner size="sm" /> Loading history...</div>
                                                    ) : (
                                                        <>
                                                            <div className="table-responsive">
                                                                <table className="table table-hover mb-0">
                                                                    <thead className="table-light">
                                                                        <tr>
                                                                            <th>Device</th>
                                                                            <th>IP Address</th>
                                                                            <th className="text-nowrap">Last Active</th>
                                                                            <th className="text-nowrap">Ended</th>
                                                                            <th>Reason</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {historyData.sessions.length > 0 ? historyData.sessions.map((s) => (
                                                                            <tr key={s.id}>
                                                                                <td>{s.device_summary || 'Unknown Device'}</td>
                                                                                <td className="text-muted">{s.ip_address || '—'}</td>
                                                                                <td className="text-nowrap text-muted">{moment(s.last_active_at).fromNow()}</td>
                                                                                <td className="text-nowrap text-muted">{s.terminated_at ? moment(s.terminated_at).format('MMM D, YYYY h:mm A') : '—'}</td>
                                                                                <td><span className="badge bg-secondary">{terminatedByLabel(s.terminated_by)}</span></td>
                                                                            </tr>
                                                                        )) : (
                                                                            <tr>
                                                                                <td colSpan="5" className="text-center text-muted py-4">No session history</td>
                                                                            </tr>
                                                                        )}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                            {historyData.totalPages > 1 && (
                                                                <div className="d-flex align-items-center justify-content-between px-3 py-2 border-top">
                                                                    <span className="text-muted fs_12">
                                                                        Page {historyData.page} of {historyData.totalPages} ({historyData.total} total)
                                                                    </span>
                                                                    <div className="d-flex gap-2">
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-secondary"
                                                                            onClick={() => setHistoryPage(p => p - 1)}
                                                                            disabled={historyData.page <= 1}
                                                                        >Prev</button>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-secondary"
                                                                            onClick={() => setHistoryPage(p => p + 1)}
                                                                            disabled={historyData.page >= historyData.totalPages}
                                                                        >Next</button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Form>
                            )}
                        </Formik>
                    </div>
                }
            </div>
        </div>
    );
};

export default AddUser;
